import WorkLines1 from '@/assets/illustrations/work-lines-1.svg'
import WorkLines2 from '@/assets/illustrations/work-lines-2.svg'
import WorkLines3 from '@/assets/illustrations/work-lines-3.svg'
import {
  fadeLeft,
  fadeRight,
  staggerContainer,
} from '@/core/constants/animations'
import { capitalizeFirstLetter } from '@/core/lib/utils/capitalize-first-letter'
import { cn } from '@/core/lib/utils/cn'
import { Motion } from '@/core/ui/atoms/motion'
import { RemoteImage } from '@/core/ui/atoms/remote-image'
import { SliderModel } from '@/features/content/types/models/slider.model'

type Props = {
  className?: string
  workSlides: SliderModel[]
  safeTripSlides: SliderModel[]
}

export const Work: React.FC<Props> = async ({
  className,
  safeTripSlides,
  workSlides,
}) => {
  const workSlide = workSlides[0]
  const safeTripSlide = safeTripSlides[0]

  if (!workSlide && !safeTripSlide) {
    return null
  }

  return (
    <article
      className={cn(
        'bg-navy relative flex flex-col gap-32 overflow-hidden py-24 lg:gap-64',
        className,
      )}
    >
      <WorkLines1 className='absolute top-0 right-0 z-0 hidden lg:block' />
      <WorkLines2 className='absolute right-32 bottom-0 z-0 hidden -scale-y-100 lg:block xl:right-40 xl:bottom-10' />
      <WorkLines3 className='absolute bottom-0 -left-10 z-0 hidden lg:block' />

      {workSlide ? (
        <Motion
          type='section'
          className='z-10 flex flex-col gap-8 lg:flex-row lg:items-center lg:px-20 xl:px-10'
          variants={staggerContainer}
          initial='initial'
          whileInView='animate'
          viewport={{ once: true, amount: 0.3 }}
        >
          <Motion
            className='flex-1'
            variants={fadeLeft.variants}
            transition={fadeLeft.transition}
            initial='initial'
            whileInView='animate'
            viewport={{ once: true }}
          >
            <h5 className='text-red text-5xl font-bold'>
              {capitalizeFirstLetter(workSlide.title)}
            </h5>
            <span
              className='mt-6 text-white'
              dangerouslySetInnerHTML={{ __html: workSlide.description }}
            />
          </Motion>

          <Motion
            className='flex flex-1 lg:justify-end'
            variants={fadeRight.variants}
            transition={fadeRight.transition}
          >
            <RemoteImage
              alt=''
              width={600}
              height={600}
              src={workSlide.image.url}
              className='rounded-lg border-8 border-solid border-white xl:h-64'
            />
          </Motion>
        </Motion>
      ) : null}

      {safeTripSlide ? (
        <Motion
          type='section'
          className='z-10 flex flex-col-reverse gap-8 lg:flex-row lg:items-center lg:px-20 xl:px-10'
          variants={staggerContainer}
          initial='initial'
          whileInView='animate'
          viewport={{ once: true, amount: 0.3 }}
        >
          <Motion
            className='flex-1'
            variants={fadeLeft.variants}
            transition={fadeLeft.transition}
            initial='initial'
            whileInView='animate'
            viewport={{ once: true }}
          >
            <RemoteImage
              alt=''
              width={600}
              height={600}
              src={safeTripSlide.image.url}
              className='rounded-lg border-8 border-solid border-white xl:h-64'
            />
          </Motion>

          <Motion
            className='flex-1'
            variants={fadeRight.variants}
            transition={fadeRight.transition}
          >
            <h4 className='text-red text-5xl font-bold'>
              {capitalizeFirstLetter(safeTripSlide.title)}
            </h4>
            <span
              className='mt-6 text-white'
              dangerouslySetInnerHTML={{ __html: safeTripSlide.description }}
            />
          </Motion>
        </Motion>
      ) : null}
    </article>
  )
}
